import type {ColourResponseBody, TrimResponseBody} from '@dms/api/new-vehicle';

import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import {offeredTrimChanges} from './offeredTrimChanges';
import {pricedItemGroupKeys} from './pricedItemGroupKeys';

type Trim = Pick<TrimResponseBody, 'id' | 'name' | 'publicationStatus'>;

/** Every row of the colour on an online trim leaves the price list. */
const archivedRows = (colour: ColourResponseBody, trims: Trim[]) =>
  offeredTrimChanges({
    resourceType: 'colourTrim',
    trims,
    stored: {name: colour.name, rows: colour.trims, isMember: true},
    next: {name: colour.name, rows: [], isMember: false},
  });

/**
 * The rows of a colour archive and its entry for an open group. The entry reads the colour when the
 * group is confirmed and sends that version and lists those rows, so a write to the colour outside
 * the group does not make the batch stale. Its name and code are free in the batch after it.
 */
export const colourArchiveChange = (
  row: ColourResponseBody,
  trims: Trim[],
  readColour: (colourId: string) => Promise<ColourResponseBody>
) => ({
  items: archivedRows(row, trims),
  entry: {
    key: pricedItemGroupKeys.colour(row.id),
    title: row.name,
    usedValues: [
      ...pricedItemGroupKeys.usedValue('colour', 'name', row.name),
      ...pricedItemGroupKeys.usedValue('colour', 'code', row.factoryCode),
    ],
    build: () =>
      readColour(row.id).then((colour) => ({
        items: archivedRows(colour, trims),
        operations: [
          {type: 'colour.archive' as const, targetId: row.id, body: {version: colour.version}},
        ],
      })),
  } satisfies Omit<PricedItemBatchEntry, 'items'>,
});
